import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import Highlighter from 'react-highlight-words';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Flex,
  Input,
  Radio,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownloadOutlined,
  HomeOutlined,
  QuestionOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import SEO from './SEO';
import compareTwoValues from '../common/compareTwoValues';
import {
  accessAll,
  accessEnt,
  accessExt,
  colourPalette,
  dateFormat,
  filterTimeout,
  notificationTimeout,
  notificationType,
  query100,
  scrollHeight,
} from '../common/constants';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import { db } from '../firebase';
import './style.scss';

export default function VehicleAccessRecord({ site }) {
  const [data, setData] = useState([]);
  const [filterDate, setFilterDate] = useState({ end: '', start: '' }); // date filter
  const [filterOption, setFilterOption] = useState(accessAll); // options filter
  const [loading, setLoading] = useState(false);
  const [searchColumn, setSearchColumn] = useState('');
  const [searchText, setSearchText] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  const fileDate = (date) =>
    date !== '' ? `_${date.split('-').join('')}` : '';
  const filename = `vmsVehicle${fileDate(filterDate.start)}${fileDate(filterDate.end)}.csv`;
  const headers = [
    { label: 'Date Time', key: 'dateTime' },
    { label: 'Access Type', key: 'accessType' },
    { label: 'Camera ID', key: 'camID' },
    { label: 'Plate Number', key: 'plateNum' },
    { label: 'Visit Type', key: 'visitType' },
  ];
  const searchInput = useRef(null);
  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'record')}`,
    total: data.length,
  };

  // destructure imports
  const { RangePicker } = DatePicker;
  const { highlighted, searchActive } = colourPalette;
  const { timeoutInfo, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeInfo, typeSuccess, typeWarning } = notificationType;
  const { id } = site;

  const filterIconColour = (filtered) => (
    <SearchOutlined style={{ color: filtered ? searchActive : undefined }} />
  );

  const filterMenu = (
    clearFilters,
    close,
    confirm,
    dataIndex,
    handleReset,
    handleSearch,
    inputSearch, // searchInput
    selectedKeys,
    // setSearchColumn,
    // setSearchText,
    setSelectedKeys,
  ) => (
    <div className="search-dialog">
      <Input
        id="search-input"
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(true, confirm, dataIndex, selectedKeys)
        }
        placeholder={`Search ${dataIndex}`}
        ref={inputSearch}
        value={selectedKeys[0]}
      />
      <Space>
        <Button
          className="search-button"
          icon={<SearchOutlined />}
          onClick={() => handleSearch(true, confirm, dataIndex, selectedKeys)}
          size="small"
          type="primary"
        >
          Search
        </Button>
        <Button
          className="search-button"
          onClick={() => handleReset(clearFilters, confirm, dataIndex)}
          size="small"
        >
          Reset
        </Button>
        <Button
          onClick={() => handleSearch(false, confirm, dataIndex, selectedKeys)}
          size="small"
          type="link"
        >
          Filter
        </Button>
        <Button onClick={() => close()} size="small" type="link">
          Close
        </Button>
      </Space>
    </div>
  );

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchRecords = useCallback(
    (dateFilter, optionFilter, siteId) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);

      const records = [];
      const accessRef = collection(db, 'vehicleAccessRecords');
      let q = query(accessRef, where('siteId', '==', siteId));

      // add access type filter (if applicable)
      if (optionFilter !== accessAll) {
        q = query(q, where('accessType', '==', optionFilter));
      }

      // add date filters if applicable, otherwise add query limit
      if (dateFilter.start !== '' && dateFilter.end !== '') {
        q = query(
          q,
          where('accessDate', '>=', dateFilter.start),
          where('accessDate', '<=', dateFilter.end),
        );
      } else {
        q = query(q, limit(query100));
      }

      // order query results
      q = query(q, orderBy('accessDate', 'desc'));

      getDocs(q)
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const record = {};
            const dict = doc.data();

            record.accessType = dict.accessType;
            record.camID = dict.camID;
            record.dateTime = `${dict.accessDate} ${dict.accessTime}`;
            record.plateNum = dict.plateNum;
            record.visitType = dict.visitType;

            records.push(record);
          });

          if (records?.length) {
            openMessage(
              `fetching latest ${records.length} ${pluralise(records.length, 'record')}`,
              timeoutSuccess,
              typeSuccess,
            );
          } else {
            console.warn('-- No record');
            openMessage('No record', timeoutInfo, typeInfo);
          }

          setData(records);
          setLoading(false);
        })
        .catch((error) => {
          setData([]);
          setLoading(false);
          console.warn('-- Error getting documents:', error);
          openMessage('Error getting documents', timeoutWarning, typeWarning);
        });
    },
    [
      openMessage,
      timeoutInfo,
      timeoutSuccess,
      timeoutWarning,
      typeInfo,
      typeSuccess,
      typeWarning,
    ],
  );

  const handleDatePickerChange = (dates) => {
    if (dates === null) {
      setFilterDate({ start: '', end: '' });
    } else {
      const date1 = dayjs(dates[0]).format(dateFormat);
      const date2 = dayjs(dates[1]).format(dateFormat);
      setFilterDate({ start: date1, end: date2 });
    }
  };

  const handleReload = () => {
    fetchRecords(filterDate, filterOption, id);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    confirm({ closeDropdown: false }); // dropdown stays open after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(''); // reset search terms
  };

  const handleSearch = (closeDropdown, confirm, dataIndex, selectedKeys) => {
    confirm({ closeDropdown }); // whether dropdown should close after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(selectedKeys[0]); // the search terms
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      clearFilters,
      close,
      confirm,
      selectedKeys,
      setSelectedKeys,
    }) =>
      filterMenu(
        clearFilters,
        close,
        confirm,
        dataIndex,
        handleReset,
        handleSearch,
        searchInput,
        selectedKeys,
        // setSearchColumn,
        // setSearchText,
        setSelectedKeys,
      ),
    filterIcon: (filtered) => filterIconColour(filtered),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), filterTimeout);
        }
      },
    },
    render: (text) =>
      searchColumn === dataIndex ? (
        <Highlighter
          autoEscape
          highlightStyle={{ backgroundColor: highlighted, padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Date Time',
      dataIndex: 'dateTime',
      width: 120,
      sortDirections: ['descend', 'ascend'],
      sorter: {
        compare: (a, b) => compareTwoValues(a.dateTime, b.dateTime),
        multiple: 1,
      },
    },
    {
      title: 'Access Type',
      dataIndex: 'accessType',
      align: 'center',
      width: 120,
      render: (tag) =>
        tag ? (
          <Tag
            bordered={false}
            className="table icon-tag"
            color={tag === accessEnt ? 'blue' : 'orange'}
            icon={
              tag === accessEnt ? <ArrowUpOutlined /> : <ArrowDownOutlined />
            }
          >
            {tag.toUpperCase()}
          </Tag>
        ) : (
          <Tag bordered={false} icon={<QuestionOutlined />}>
            --
          </Tag>
        ),
    },
    {
      title: 'Camera ID',
      dataIndex: 'camID',
      width: 200,
      render: (camID) => <code className="mono">{camID}</code>,
    },
    {
      title: 'Plate Number',
      dataIndex: 'plateNum',
      width: 200,
      ...getColumnSearchProps('plateNum'),
    },
    {
      title: 'Visit Type',
      dataIndex: 'visitType',
      width: 120,
      filters: [
        {
          text: 'Contractor',
          value: 'Contractor',
        },
        {
          text: 'Personal',
          value: 'Personal',
        },
        {
          text: 'Visitor',
          value: 'Visitor',
        },
        {
          text: 'Resident',
          value: 'Resident',
        },
      ],
      onFilter: (value, record) => record.visitType === value,
    },
  ];

  useEffect(
    () => fetchRecords(filterDate, filterOption, id),
    [fetchRecords, filterDate, filterOption, id],
  );

  return (
    <>
      <SEO
        description="Review vehicle access records"
        name="qiwei"
        title="Access Records (Vehicle)"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Access Records (Vehicle)</span>,
            },
          ]}
        />
        <Flex align="center" wrap>
          <RangePicker
            format={dateFormat}
            id="rangePicker"
            name="rangePicker"
            onChange={handleDatePickerChange}
            showNow
            variant="borderless"
          />
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={handleReload}
            type="link"
          >
            Reload
          </Button>
          <CSVLink
            className="csv download-button"
            data={data}
            enclosingCharacter=""
            filename={filename}
            headers={headers}
          >
            <DownloadOutlined />
            <span className="text">Download Report (CSV)</span>
          </CSVLink>
          <Radio.Group
            buttonStyle="solid"
            defaultValue={accessAll}
            id="radioGroup"
            name="accessFilter"
            onChange={(e) => setFilterOption(e.target.value)}
          >
            <Radio.Button value={accessAll}>
              {accessAll.toUpperCase()}
            </Radio.Button>
            <Radio.Button value={accessEnt}>
              {accessEnt.toUpperCase()}
            </Radio.Button>
            <Radio.Button value={accessExt}>
              {accessExt.toUpperCase()}
            </Radio.Button>
          </Radio.Group>
        </Flex>
      </div>
      {contextHolder}
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="dateTime"
        scroll={{ x: 760, y: scrollHeight }}
      />
    </>
  );
}

VehicleAccessRecord.propTypes = {
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};
